<template>
	<section class="charts-container">
		<div v-if="isLoading" class="loading-container">
			<div class="loading-spinner"></div>
			<p class="loading-text">{{ $t('loading_charts') }}</p>
		</div>

		<div v-else class="charts-wrapper">
			<!-- Desktop Table Layout -->
			<table v-if="useDesktopChartLayout" class="charts-table desktop-layout" role="table" :aria-label="$t('feedback_charts')">
				<thead>
					<tr role="row">
						<th
							v-if="chartListData.general && chartListData.general.labels && chartListData.general.labels.length"
							role="columnheader"
							:aria-label="$t('general_overview')"
							tabindex="0"
						>
						<div class="header-content">
							<span class="header-title">{{ $t('general') }}</span>
							<span class="header-subtitle">{{ generalCount }} {{ $t('items') }} <span class="total-note">({{ $t('total') }})</span></span>
						</div>
						</th>
						<th
							v-if="chartListData.cleaning && chartListData.cleaning.labels && chartListData.cleaning.labels.length"
							role="columnheader"
							:aria-label="$t('cleaning_complaints_breakdown')"
							tabindex="0"
						>
						<div class="header-content">
							<span class="header-title">{{ $t('cleaning_complaints') }}</span>
							<span class="header-subtitle">{{ cleaningCount }} {{ $t('items') }} <span class="total-note">({{ $t('total') }})</span></span>
						</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-if="chartListData.general" role="row">
						<td
							v-for="(chartData, key) in chartListData"
							:key="key"
							v-if="key !== 'maintenance'"
							class="chart-cell"
							:class="{ 'interactive-chart': true }"
							role="gridcell"
						>
							<div class="chart-wrapper">
								<Chart
									:isClickable="true"
									v-if="chartData.labels && chartData.labels.length"
									type="doughnut"
									:labels="chartData.labels"
									:datasets="chartData.datasets"
									:options="isPrintMode ? getPdfChartOptions() : getChartOptions(key)"
									:height="chartCanvasHeight"
									:width="chartCanvasWidth"
									:isPlugin="true"
									@click="(data) => handleChartClick(data, key)"
									:ref="'chartContainer' + key"
									@doneRender="handleChartRender"
								/>

								<div v-if="chartData.labels && chartData.labels.length > 1" class="chart-legend">
									<div class="legend-header" @click="toggleLegend(key)">
										<span class="legend-title">{{ $t('more_details') }}</span>
										<span class="legend-toggle" :class="{ 'expanded': expandDetails || (!isLegendCollapsed[key] && isLegendCollapsed[key] !== undefined) }">
											<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
												<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
											</svg>
										</span>
									</div>
									<div class="legend-content" :class="{ 'collapsed': expandDetails ? false : (isLegendCollapsed[key] === undefined || isLegendCollapsed[key]) }">
										<div
											v-for="(label, index) in chartData.labels"
											:key="index"
											class="legend-item"
											:style="{
												'--legend-color':
													chartData.datasets[0].backgroundColor[index] ||
													chartData.datasets[0].backgroundColor
											}"
										>
											<span class="legend-color"></span>
											<span class="legend-text">{{ label }}</span>
											<span class="legend-value">{{ Math.round(chartData.datasets[0].data[index]) }}</span>
										</div>
									</div>
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<!-- Mobile Card Layout - Only rendered on mobile -->
			<div v-if="!useDesktopChartLayout" class="charts-grid mobile-layout">
				<div
					v-for="(chartData, key) in chartListData"
					:key="key"
					v-if="key !== 'maintenance'"
					class="chart-card"
					:class="{ 'interactive-chart': key !== 'general' }"
				>
					<div class="chart-card-header">
						<div class="header-content">
							<span class="header-title">
								{{ key === 'general' ? $t('general') : $t(key + '_complaints') }}
							</span>
							<span class="header-subtitle">
								{{ key === 'general' ? generalCount + ' ' + $t('items') : cleaningCount + ' ' + $t('items') }} <span class="total-note">({{ $t('total') }})</span>
							</span>
						</div>
					</div>

					<div class="chart-wrapper">
						<Chart
							:isClickable="true"
							v-if="chartData.labels && chartData.labels.length"
							type="doughnut"
							:labels="chartData.labels"
							:datasets="chartData.datasets"
							:options="getResponsiveChartOptions(key)"
							height="200px"
							width="100%"
							:isPlugin="true"
							@click="(data) => handleChartClick(data, key)"
							:ref="'chartContainer' + key + 'Mobile'"
							@doneRender="handleChartRender"
							:aria-label="getChartAriaLabel(key, chartData)"
						/>

						<!-- Show legend on mobile -->
						<div v-if="chartData.labels && chartData.labels.length > 1" class="chart-legend mobile-legend">
							<div class="legend-header" @click="toggleLegend(key + 'Mobile')">
								<span class="legend-title">{{ $t('more_details') }}</span>
								<span class="legend-toggle" :class="{ 'expanded': expandDetails || (!isLegendCollapsed[key + 'Mobile'] && isLegendCollapsed[key + 'Mobile'] !== undefined) }">
									<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
										<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
								</span>
							</div>
							<div class="legend-content" :class="{ 'collapsed': expandDetails ? false : (isLegendCollapsed[key + 'Mobile'] === undefined || isLegendCollapsed[key + 'Mobile']) }">
								<div
									v-for="(label, index) in chartData.labels"
									:key="index"
									class="legend-item"
									:style="{
										'--legend-color':
											chartData.datasets[0].backgroundColor[index] || chartData.datasets[0].backgroundColor
									}"
								>
									<span class="legend-color"></span>
									<span class="legend-text">{{ label }}</span>
									<span class="legend-value">{{ Math.round(chartData.datasets[0].data[index]) }}</span>
								</div>
							</div>
						</div>

						<div v-else class="empty-state">
							<div class="empty-icon">📊</div>
							<p class="empty-text">{{ $t('no_data_available') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import Chart from '@/modules/common/components/Chart'
import { capitalize } from '@/modules/common/services/utilService'
import utilService from '../../common/services/utilService'
import scssVars from '@/styles/vars.scss'
import { analyticsChartColors } from '../consts/consts'

export default {
	data() {
		return {
			chartColors: analyticsChartColors,
			chartListData: {},
			isLoading: true,
			chartsToRender: 0,
			chartsRendered: 0,
			isMobile: false,
			windowWidth: window.innerWidth,
			isLegendCollapsed: {},
			generalChartLabelToKeyMap: {},
			baseChartOptions: {
				centerText: { display: true, text: 'No DATA' },
				elements: { center: { text: 'TEST' } },
				legend: { position: 'right', display: false },
				layout: { padding: { top: 15, bottom: 15 } }, // Reduced padding
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					animateRotate: true,
					animateScale: true,
					duration: 800,
					easing: 'easeOutQuart'
				},
				hover: {
					onHover: function (e) {
						var point = this.getElementAtEvent(e)
						if (point.length) {
							e.target.style.cursor = 'pointer'
							// Add subtle scale effect
							this.options.animation.animateScale = true
						} else {
							e.target.style.cursor = 'default'
						}
					},
					animationDuration: 200
				},
				tooltips: {
					enabled: true,
					mode: 'single',
					backgroundColor: scssVars.lightGrey,
					titleFontColor: scssVars.blackBlue,
					bodyFontColor: scssVars.blackBlue,
					titleFontSize: 14,
					bodyFontSize: 14,
					cornerRadius: 8,
					xPadding: 12,
					yPadding: 12,
					displayColors: true,
					callbacks: {
						title: (tooltipItems, data) => {
							return data.labels[tooltipItems[0].index]
						},
						label: (item, tableData) => {
							const value = Math.round(tableData.datasets[0].data[item.index])
							return `${value}`
						}
					}
				},
				plugins: {
					datalabels: {
						labels: {
							font: { size: '0.875rem', weight: 'bold' },
							display: 'auto'
						},
						color: '#ffffff',
						opacity: 1,
						anchor: 'center',
						align: 'center',
						offset: 0,
						formatter: (value, context) => {
							const roundedValue = Math.round(value)
							if (!context.chart.data.datasets[0]._meta) return
							const totalIndex = Object.keys(context.chart.data.datasets[0]._meta)[0]
							const total = context.chart.data.datasets[0]._meta[totalIndex].total
							let percentageNum = Math.round((roundedValue / total) * 100)

							return `${percentageNum}%`
						}
					}
				}
			}
		}
	},
	props: {
		data: { type: Object, required: true },
		expandDetails: { type: Boolean, default: false }
	},
	computed: {
		/** PDF export subtree: always desktop table + fixed chart px so capture does not depend on window size. */
		useDesktopChartLayout() {
			return !this.isMobile || this.expandDetails
		},
		chartCanvasHeight() {
			if (this.expandDetails) return '300px'
			if (this.isMobile) return '200px'
			return '25vh'
		},
		chartCanvasWidth() {
			if (this.expandDetails) return '420px'
			if (this.isMobile) return '100%'
			return '25vw'
		},
		isPrintMode() {
			return document.body.classList.contains('print-mode')
		},
		totalFeedbackCount() {
			let total = 0
			for (let key in this.data) {
				if (key !== 'maintenance') {
					if (this.data[key].data) {
						total += this.data[key].data.reduce((a, b) => a + b, 0)
					} else {
						total += this.data[key]
					}
				}
			}
			return total
		},
		complaintsRatio() {
			const complaints = this.data.cleaning?.data?.reduce((a, b) => a + b, 0) || this.data.cleaning || 0
			const total = this.totalFeedbackCount
			return total > 0 ? Math.round((complaints / total) * 100) : 0
		},
		hasComplaintData() {
			return this.complaintsRatio > 0
		},
		hasInteractiveCharts() {
			return this.chartListData.cleaning?.labels?.length > 0
		},
		cleaningCount() {
			return this.data.cleaning?.data?.reduce((a, b) => a + b, 0) || this.data.cleaning || 0
		},
		generalCount() {
			// Sum all items in the general chart (likes + cleaning + maintenance)
			// Calculate from raw data, same way as the general chart is built
			let total = 0
			if (this.data) {
				// Sum cleaning
				if (this.data.cleaning?.data) {
					total += this.data.cleaning.data.reduce((a, b) => Number(a) + Number(b), 0)
				} else if (this.data.cleaning) {
					total += Number(this.data.cleaning) || 0
				}
				// Sum totalLikes
				if (this.data.totalLikes?.data) {
					total += this.data.totalLikes.data.reduce((a, b) => Number(a) + Number(b), 0)
				} else if (this.data.totalLikes) {
					total += Number(this.data.totalLikes) || 0
				}
				// Sum maintenance
				if (this.data.maintenance?.data) {
					total += this.data.maintenance.data.reduce((a, b) => Number(a) + Number(b), 0)
				} else if (this.data.maintenance) {
					total += Number(this.data.maintenance) || 0
				}
			}
			return total
		}
	},
	created() {
		this.checkIfMobile()
		this.updateComponent()
	},
	mounted() {
		window.addEventListener('resize', this.handleResize)
		// Use ResizeObserver if available for better performance
		if (window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(this.handleResize)
			this.resizeObserver.observe(document.body)
		}
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
		}
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout)
		}
	},
	methods: {
		checkIfMobile() {
			// You can adjust this breakpoint to match your needs
			const mobileBreakpoint = 1024
			this.isMobile = window.innerWidth < mobileBreakpoint
		},
		toggleLegend(key) {
			// Initialize as collapsed if not set
			if (this.isLegendCollapsed[key] === undefined) {
				this.$set(this.isLegendCollapsed, key, true)
			} else {
				this.$set(this.isLegendCollapsed, key, !this.isLegendCollapsed[key])
			}
		},
		handleResize() {
			if (this.expandDetails) return
			// Debounce resize events for better performance
			if (this.resizeTimeout) {
				clearTimeout(this.resizeTimeout)
			}

			this.resizeTimeout = setTimeout(() => {
				const newWidth = window.innerWidth
				const wasMobile = this.isMobile
				this.checkIfMobile()

				// Only update if crossing the mobile/desktop boundary
				if (wasMobile !== this.isMobile) {
					this.windowWidth = newWidth
					// Reset chart render tracking when switching modes
					this.chartsRendered = 0
					this.updateChartsToRender()
				}
			}, 250) // 250ms debounce
		},
		updateChartsToRender() {
			this.chartsToRender = 0

			for (let key in this.chartListData) {
				if (key !== 'maintenance' && this.chartListData[key].labels && this.chartListData[key].labels.length) {
					// Only count the charts that will actually be rendered
					this.chartsToRender += 1
				}
			}
		},
		getChartOptions(key) {
			const options = { ...this.baseChartOptions }
			if (key === 'general') {
				// General chart is now clickable, so keep hover effects
				options.animation.duration = 1000
			}
			return options
		},
		getResponsiveChartOptions(key) {
			const options = { ...this.baseChartOptions }

			// Mobile-specific adjustments - reduced padding
			options.layout.padding = { top: 10, bottom: 10 }
			options.plugins.datalabels.labels.font.size = '0.75rem'
			options.tooltips.titleFontSize = 12
			options.tooltips.bodyFontSize = 12

			if (key === 'general') {
				// General chart is now clickable, so keep hover effects
				options.animation.duration = 1000
			}
			return options
		},
		getChartAriaLabel(key, chartData) {
			const total = chartData.datasets[0].data.reduce((a, b) => a + b, 0)
			return `${capitalize(key)} chart with ${total} total items across ${chartData.labels.length} categories`
		},
		handleChartClick(chartData, chartKey) {
			if (navigator.vibrate) {
				navigator.vibrate(50)
			}
			
			// Handle general chart clicks differently
			if (chartKey === 'general') {
				const clickedLabel = chartData.label
				// Map the clicked label back to its original key
				const originalKey = this.generalChartLabelToKeyMap[clickedLabel]
				
				// Emit a special event for general chart segment clicks
				this.$emit('generalChartClick', {
					label: clickedLabel,
					value: chartData.value,
					originalKey: originalKey
				})
			} else {
				// For other charts (cleaning), emit the normal chartClick event
				this.$emit('chartClick', chartData)
			}
		},
		handleChartRender() {
			this.chartsRendered++

			if (this.chartsRendered >= this.chartsToRender) {
				this.isLoading = false

				this.$nextTick(() => {
					setTimeout(() => {
						this.$emit('mounted')
					}, 500) // give charts time to render
				})
			}
		},
		// Helper method to assign colors based on label content
		getColorsForLabels(labels, colorKey, labelToKeyMap = null) {
			const availableColors = this.chartColors[colorKey] || ['#E9E9E9']
			const colorMap = {}
			const assignedColors = []
			
			// Create a mapping of labels to colors based on their content
			labels.forEach((label, index) => {
				const lowerLabel = label.toLowerCase()
				
				// For general chart (analyticsChartTable), map specific labels to specific colors
				if (colorKey === 'analyticsChartTable') {
					// Use the original key if available (most reliable method)
					if (labelToKeyMap && labelToKeyMap[label]) {
						const originalKey = labelToKeyMap[label]
						if (originalKey === 'totalLikes') {
							colorMap[label] = availableColors[0] // First color for likes (green)
						} else if (originalKey === 'cleaning') {
							colorMap[label] = availableColors[1] // Second color for cleaning/complaints (red)
						} else if (originalKey === 'maintenance') {
							colorMap[label] = availableColors[2] // Third color for maintenance (blue)
						} else {
							// Fallback to index-based assignment
							colorMap[label] = availableColors[index % availableColors.length]
						}
					} else {
						// Fallback: Check against translated strings to support all languages
						const likeTranslation = this.$t('like') || 'like'
						const cleaningTranslation = this.$t('cleaning') || 'cleaning'
						const maintenanceTranslation = this.$t('maintenance') || 'maintenance'
						const likeLower = likeTranslation.toLowerCase()
						const cleaningLower = cleaningTranslation.toLowerCase()
						const maintenanceLower = maintenanceTranslation.toLowerCase()
						
						// Check if label matches the translated "like" (works for all languages)
						if (lowerLabel === likeLower || lowerLabel.includes(likeLower) || 
							lowerLabel.includes('like') || lowerLabel.includes('likes')) {
							colorMap[label] = availableColors[0] // First color for likes (green)
						} else if (lowerLabel === cleaningLower || lowerLabel.includes(cleaningLower) ||
							lowerLabel.includes('clean') || lowerLabel.includes('complaint')) {
							colorMap[label] = availableColors[1] // Second color for cleaning/complaints (red)
						} else if (lowerLabel === maintenanceLower || lowerLabel.includes(maintenanceLower) ||
							lowerLabel.includes('maintenance')) {
							colorMap[label] = availableColors[2] // Third color for maintenance (blue)
						} else {
							// Fallback to index-based assignment
							colorMap[label] = availableColors[index % availableColors.length]
						}
					}
				} else {
					// For other charts, use index-based assignment
					colorMap[label] = availableColors[index % availableColors.length]
				}
				
				assignedColors.push(colorMap[label])
			})
			
			return assignedColors
		},

		updateComponent() {
			if (!this.data) {
				this.isLoading = false
				return
			}

		const chartListData = {
			general: { labels: [], datasets: [] },
			cleaning: { labels: [], datasets: [] },
		}
		let signals = { cleaning: 0, maintenance: 0, totalLikes: 0 }

		for (let key in this.data) {
			if (key !== 'maintenance') {
				if (this.data[key].data) {
					this.data[key].data.forEach((a) => (signals[key] = signals[key] + a))

						let labels
						if (this.data[key].localLabels && this.data[key].localLabels[0]) {
							labels = this.data[key].localLabels
						} else if (this.data[key].labels && this.data[key].labels[0]) {
							labels = this.data[key].labels
						} else {
							labels = []
						}

						// Use utilService.sortParallelArrays to sort data and labels together
						if (labels.length > 0 && this.data[key].data.length > 0) {
							const dataArray = this.data[key].data.map((val) => Math.round(val))
							const sortedData = utilService.sortParallelArrays(dataArray, labels)

							chartListData[key] = {
								labels: sortedData.map((item) => item.str),
								datasets: [
									{
										data: sortedData.map((item) => item.num),
										backgroundColor: sortedData.length > 0 ? this.getColorsForLabels(sortedData.map((item) => item.str), key) : ['#E9E9E9'],
										borderWidth: 0,
										hoverBorderWidth: 2,
										hoverBorderColor: '#ffffff'
									}
								]
							}
						} else {
							// Fallback for empty data
							chartListData[key] = {
								labels: labels,
								datasets: [
									{
										data: this.data[key].data[0] ? this.data[key].data.map((val) => Math.round(val)) : [1],
										backgroundColor: this.data[key].data[0] ? this.getColorsForLabels(labels, key) : ['#E9E9E9'],
										borderWidth: 0,
										hoverBorderWidth: 2,
										hoverBorderColor: '#ffffff'
									}
								]
							}
						}
				} else {
					signals[key] = signals[key] + this.data[key]
				}
			}
		}

		// Process maintenance data separately (it's excluded from the main loop but should be in general chart)
		if (this.data.maintenance) {
			if (this.data.maintenance.data) {
				this.data.maintenance.data.forEach((a) => (signals.maintenance = signals.maintenance + a))
			} else {
				signals.maintenance = signals.maintenance + this.data.maintenance
			}
		}

		// Remove maintenance from signals if it's 0
		if (signals.maintenance === 0 || Math.round(signals.maintenance) === 0) {
			delete signals.maintenance
		}

		// Create and sort the general chart data (likes vs complaints vs maintenance)
		// Create a mapping of keys to labels to preserve key information for color assignment
		const signalsKeys = Object.keys(signals).filter(key => {
			// Filter out maintenance if it's 0
			if (key === 'maintenance') {
				const value = signals[key]
				return value && value !== 0 && Math.round(value) !== 0
			}
			return true
		})
		const signalsLabels = signalsKeys.map((key) => {
			if (key === 'totalLikes') return this.$t('like') || 'Likes'
			else if (key === 'maintenance') return capitalize(this.$t('maintenance') || 'Maintenance')
			else return capitalize(this.$t(key) || key)
		})
		const signalsValues = signalsKeys.map(key => Math.round(signals[key]))

		// Create a mapping of labels to their original keys BEFORE sorting
		const labelToKeyMap = {}
		signalsKeys.forEach((key, index) => {
			labelToKeyMap[signalsLabels[index]] = key
		})

		// Define fixed order for general chart: likes, cleaning, maintenance
		const fixedOrder = ['totalLikes', 'cleaning', 'maintenance']
		
		// Create array with order priority
		const signalsWithOrder = signalsKeys.map((key, index) => {
			const orderIndex = fixedOrder.indexOf(key)
			return {
				key: key,
				label: signalsLabels[index],
				value: signalsValues[index],
				order: orderIndex >= 0 ? orderIndex : 999 // Put unknown keys at the end
			}
		})
		
		// Sort by fixed order (not by value)
		const sortedSignals = signalsWithOrder.sort((a, b) => a.order - b.order)
		
		// Filter out any items with 0 values and format like sortParallelArrays output
		const filteredSortedSignals = sortedSignals
			.filter(item => item.value > 0)
			.map(item => ({
				num: item.value,
				str: item.label
			}))

		// Create mapping of sorted labels to original keys
		// Map each sorted label back to its original key using the pre-sort labelToKeyMap
		const sortedLabelToKeyMap = {}
		filteredSortedSignals.forEach((item) => {
			// The item.str is the sorted label, which should match one of the original labels
			// Use the labelToKeyMap we created before sorting
			if (labelToKeyMap[item.str]) {
				sortedLabelToKeyMap[item.str] = labelToKeyMap[item.str]
			}
		})
		
		// Store the mapping for use in click handler
		this.generalChartLabelToKeyMap = sortedLabelToKeyMap

		const likesProblemsChart = {
			labels: filteredSortedSignals.map((item) => item.str),
			datasets: [
				{
					data: filteredSortedSignals.map((item) => item.num),
					backgroundColor: this.getColorsForLabels(
						filteredSortedSignals.map((item) => item.str), 
						'analyticsChartTable',
						sortedLabelToKeyMap
					),
					borderWidth: 0,
					hoverBorderWidth: 2,
					hoverBorderColor: '#ffffff'
				}
			]
		}

		const hasGeneralData = signals.cleaning > 0 || signals.totalLikes > 0 || (signals.maintenance && signals.maintenance > 0)

			if (!hasGeneralData) {
				chartListData.general = {
					labels: [],
					datasets: []
				}
			} else {
				chartListData.general = likesProblemsChart
			}

			this.updateChartsToRender()
			this.chartsRendered = 0

			this.chartListData = chartListData
			this.$store.commit('setChartListData', chartListData)

			setTimeout(() => {
				this.isLoading = false
				if (this.chartsToRender === 0) {
					this.$nextTick(() => {
						setTimeout(() => this.$emit('mounted'), 100)
					})
				}
			}, 300)
		},
		getPdfChartOptions() {
			const options = { ...this.baseChartOptions }
			
			// Disable animations for PDF
			options.animation = {
				duration: 0, // No animation during PDF generation
				animateRotate: false,
				animateScale: false
			}
			
			// Simplify hover effects for PDF
			options.hover = {
				animationDuration: 0,
				onHover: null // Disable hover effects
			}
			
			// Ensure high contrast for PDF
			options.plugins.datalabels = {
				...options.plugins.datalabels,
				color: '#000000', // Force black text
				font: {
				size: '14px',
				weight: 'bold'
				}
			}
			return options
		}
	},
	components: { Chart }
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.charts-container {
	position: relative;
	min-height: 200px;
	margin-bottom: 16px;
	width: 100%;

	@media (max-width: 768px) {
		margin-bottom: 12px;
		min-height: 150px;
	}
}

.loading-container {
	@include flex-center;
	flex-direction: column;
	height: 200px;

	@media (max-width: 768px) {
		height: 150px;
	}

	.loading-spinner {
		width: 40px;
		height: 40px;
		border: 3px solid #f3f3f3;
		border-top: 3px solid #3498db;
		border-radius: 50%;
		animation: spin 1s linear infinite;

		@media (max-width: 768px) {
			width: 32px;
			height: 32px;
			border-width: 2px;
		}
	}

	.loading-text {
		margin-top: 16px;
		color: #666;
		font-size: 14px;

		@media (max-width: 768px) {
			font-size: 12px;
			margin-top: 12px;
		}
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.charts-wrapper {
	animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

// ========================================
// DESKTOP LAYOUT (TABLE)
// ========================================

.desktop-layout {
	display: table !important;

	@media (max-width: 1023px) {
		display: none !important;
	}
}

.charts-table {
	width: 100%;
	min-width: 79vw;
	height: auto;
	min-height: 240px;
	border: 1px solid #dee2e6;
	border-radius: 12px;
	border-collapse: separate;
	border-spacing: 0;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	transition: box-shadow 0.3s ease;

	&:hover {
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
	}

	thead {
		background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	}

	th {
		height: auto;
		min-height: 50px; // Reduced from 60px
		padding: 12px 20px; // Reduced from 16px 24px
		text-align: left;
		border-bottom: 2px solid #dee2e6;
		border-right: 1px solid #dee2e6;
		position: relative;
		transition: background-color 0.2s ease;
		cursor: pointer;

		&:hover {
			background-color: rgba(0, 123, 255, 0.05);
		}

		&:focus {
			outline: 2px solid #007bff;
			outline-offset: -2px;
		}

		&:last-child {
			border-right: none;
		}
	}

	.header-content {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.header-title {
		font-size: 14px;
		font-weight: 600;
		color: #495057;
	}

	.header-subtitle {
		font-size: 12px;
		color: #6c757d;
		opacity: 0.8;
		
		.total-note {
			font-size: 11px;
			font-style: italic;
			opacity: 0.7;
		}
	}
}

.chart-cell {
	padding: 12px;
	vertical-align: top;
	border-right: 1px solid #dee2e6;
	transition: all 0.3s ease;

	&:last-child {
		border-right: none;
	}

	&.interactive-chart {
		cursor: pointer;

		&:hover {
			background: #f8f9fa;
			transform: translateY(-2px);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		}
	}
}

.chart-wrapper {
  @include align-center;
  flex-direction: column;
  gap: 6px;
  min-height: 180px;

  // PDF-specific adjustments
  .print-mode & {
    min-height: 400px;
    justify-content: center;
    align-items: center;
    padding: 20px;
    display: flex !important;
    
    // Ensure chart takes defined space and is centered
    .chart-container,
    > div:first-child {
      height: 240px !important;
      margin: 0 auto 20px auto !important;
      flex-shrink: 0;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
    }
    
    // Force legend below chart and center it
    .chart-legend {
      margin: 20px auto 0 auto !important;
      position: relative !important;
      z-index: 1 !important;
      background-color: white !important;
      border: 1px solid #ddd !important;
      flex-shrink: 0;
      width: fit-content !important;
      max-width: 300px !important;
      align-self: center !important;
    }
  }
}

// ========================================
// MOBILE LAYOUT (CARDS)
// ========================================

.mobile-layout {
	display: none !important;

	@media (max-width: 1023px) {
		display: block !important;
	}
}

.charts-grid {
	display: grid;
	gap: 16px; // Reduced from 20px
	width: 100%;

	// Tablet layout (2 columns)
	@media (min-width: 768px) and (max-width: 1023px) {
		grid-template-columns: 1fr 1fr;
		gap: 14px; // Reduced from 16px

		.chart-card:first-child {
			grid-column: 1 / -1; // General chart spans full width
		}
	}

	// Mobile layout (1 column)
	@media (max-width: 767px) {
		grid-template-columns: 1fr;
		gap: 10px; // Reduced from 12px
	}
}

.chart-card {
	border: 1px solid #dee2e6;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;

	&.interactive-chart {
		cursor: pointer;

		&:hover {
			transform: translateY(-4px);
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
		}

		@media (hover: none) {
			&:hover {
				transform: none;
			}

			&:active {
				transform: scale(0.98);
			}
		}
	}

	@media (max-width: 768px) {
		border-radius: 8px;

		&.interactive-chart:hover {
			transform: translateY(-2px);
		}
	}
}

.chart-card-header {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	padding: 12px 16px; // Reduced from 16px 20px
	border-bottom: 1px solid #dee2e6;

	@media (max-width: 768px) {
		padding: 10px 14px; // Reduced from 12px 16px
	}

	.header-content {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.header-title {
		font-size: 16px;
		font-weight: 600;
		color: #495057;

		@media (max-width: 768px) {
			font-size: 14px;
		}
	}

	.header-subtitle {
		font-size: 14px;
		color: #6c757d;
		opacity: 0.8;

		@media (max-width: 768px) {
			font-size: 12px;
		}
		
		.total-note {
			font-size: 11px;
			font-style: italic;
			opacity: 0.7;
			
			@media (max-width: 768px) {
				font-size: 10px;
			}
		}
	}
}

.chart-card .chart-wrapper {
	padding: 12px;
	min-height: 300px;
	gap: 6px;

	@media (max-width: 768px) {
		padding: 10px;
		min-height: 260px;
	}

	@media (max-width: 480px) {
		padding: 8px;
		min-height: 240px;
	}
}

// ========================================
// SHARED CHART ELEMENTS
// ========================================

.chart-legend {
	display: flex;
	flex-direction: column;
	margin-top: 16px;
	background: #f8f9fa;
	border-radius: 8px;
	width: 100%;
	max-width: 300px;
	border: 1px solid #e9ecef;
	overflow: hidden;

	// Show on mobile only
	&.mobile-legend {
		display: flex;
	}

	@media (max-width: 768px) {
		margin-top: 12px;
	}

	.legend-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 12px 16px;
		background: #ffffff;
		border-bottom: 1px solid #e9ecef;
		cursor: pointer;
		user-select: none;
		transition: background-color 0.2s ease;

		&:hover {
			background: #f8f9fa;
		}

		.legend-title {
			font-weight: 600;
			font-size: 14px;
			color: #495057;
		}

		.legend-toggle {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 20px;
			height: 20px;
			border-radius: 4px;
			transition: transform 0.2s ease, background-color 0.2s ease;
			color: #6c757d;

			&:hover {
				background: #e9ecef;
			}

			&.expanded {
				transform: rotate(180deg);
			}

			svg {
				transition: transform 0.2s ease;
			}
		}
	}

	.legend-content {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 16px;
		transition: all 0.3s ease;
		overflow: hidden;

		&.collapsed {
			max-height: 0;
			padding: 0 16px;
			opacity: 0;
		}

		@media (max-width: 768px) {
			padding: 12px;
			gap: 6px;

			&.collapsed {
				padding: 0 12px;
			}
		}
	}

	@media (max-width: 480px) {
		padding: 10px;
		margin-top: 10px;
		gap: 4px;
	}
}

.legend-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 6px 0;

	@media (max-width: 768px) {
		gap: 8px;
		padding: 4px 0;
	}

	.legend-color {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background-color: var(--legend-color);
		flex-shrink: 0;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

		@media (max-width: 768px) {
			width: 14px;
			height: 14px;
		}
	}

	.legend-text {
		flex: 1;
		font-size: 13px;
		color: #495057;

		@media (max-width: 768px) {
			font-size: 12px;
		}
	}

	.legend-value {
		font-weight: 600;
		font-size: 13px;
		color: #495057;
		padding: 2px 8px;
		background: #ffffff;
		border-radius: 12px;
		border: 1px solid #dee2e6;

		@media (max-width: 768px) {
			font-size: 12px;
			padding: 2px 6px;
		}
	}
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 32px 20px; // Reduced from 48px 24px
	text-align: center;

	@media (max-width: 768px) {
		padding: 24px 14px; // Reduced from 32px 16px
	}

	@media (max-width: 480px) {
		padding: 20px 10px; // Reduced from 24px 12px
	}

	.empty-icon {
		font-size: 48px;
		margin-bottom: 16px;
		opacity: 0.3;

		@media (max-width: 768px) {
			font-size: 36px;
			margin-bottom: 12px;
		}
	}

	.empty-text {
		color: #6c757d;
		font-size: 14px;
		margin: 0;

		@media (max-width: 768px) {
			font-size: 12px;
		}
	}
}

// ========================================
// RESPONSIVE CHART SIZING
// ========================================

@media (max-width: 1023px) {
	.charts-table {
		min-width: 100%;
		width: 100%;
	}
}

// ========================================
// ACCESSIBILITY & PERFORMANCE
// ========================================

@media (prefers-reduced-motion: reduce) {
	* {
		animation-duration: 0.01ms !important;
		animation-iteration-count: 1 !important;
		transition-duration: 0.01ms !important;
	}

	.chart-card {
		&:hover {
			transform: none !important;
		}
	}
}

@media (prefers-contrast: high) {
	.charts-table,
	.chart-card {
		border: 2px solid #000;
	}

	.chart-cell,
	.chart-card-header {
		border: 1px solid #000;
	}
}

// ========================================
// TOUCH DEVICE OPTIMIZATIONS
// ========================================

@media (hover: none) and (pointer: coarse) {
	.chart-cell.interactive-chart,
	.chart-card.interactive-chart {
		&:hover {
			transform: none;
			background: #ffffff;
		}

		&:active {
			transform: scale(0.98);
			transition: transform 0.1s ease;
		}
	}
}

// ========================================
// PDF GENERATION STYLES
// ========================================

// Print-mode class from print component)
.print-mode {
  // Force high contrast and visibility
  .charts-container {
    background-color: white !important;
    
    .chart-wrapper {
      background-color: white !important;
      opacity: 1 !important;
    }
    
    // Simplify layout for PDF
    .charts-table {
      border-collapse: separate !important;
      background-color: white !important;
    }
    
    .chart-cell {
      background-color: white !important;
      border: 1px solid #000 !important; // High contrast borders
    }
    
    // Remove complex styling that might interfere
    * {
      box-shadow: none !important;
      animation: none !important;
      transition: none !important;
      transform: none !important;
    }
  }
  
  // Ensure legends are visible and high contrast
  .chart-legend {
    background-color: white !important;
    border: 1px solid #ccc !important;
    
    .legend-text,
    .legend-value {
      color: #000 !important;
      font-weight: bold !important;
    }
  }
}

// ========================================
// PRINT STYLES
// ========================================

@media print {
	.charts-container {
		margin-bottom: 20px;
	}

	.mobile-layout {
		display: none !important;
	}

	.desktop-layout {
		display: table !important;
	}

	.charts-table {
		box-shadow: none;
		border: 1px solid #000;
	}

	.chart-cell {
		padding: 16px;
	}

	.chart-wrapper {
		min-height: 200px;
	}

	// Show mobile legends when printing
	.chart-legend.mobile-legend {
		display: flex !important;
		page-break-inside: avoid;
	}
}
</style>
